import { DeleteOutlined } from '@ant-design/icons';
import {
  DecimalInput,
  FormItem,
  RelationEditor,
  TextInput,
} from '@framework/form';
import { TableData } from '@framework/ui';
import { Button, Flex, Space, Table, Tooltip } from 'antd';
import * as React from 'react';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import {
  MapCadastralAreaItem,
  MapCadastralAreaItemSchema,
  MapCadastralSuffix,
} from '../createMapSchema';
import { MapUnitRender } from '../layers/rendering/MapUnitRender';
import { MapComputedTotalCadastralAffectedArea } from './computed/MapComputedTotalCadastralAffectedArea';

export interface IMapCadastralAreasTableProps {
  name: string;
  editor: RelationEditor<MapCadastralAreaItem>;
  readonly?: boolean;
}

export function MapCadastralAreasTable(props: IMapCadastralAreasTableProps) {
  const { name, editor, readonly } = props;
  const isMobile = useBreakpoint(down('lg'));
  return (
    <div style={{ overflowY: 'scroll' }}>
      <TableData
        dataSource={editor.items}
        rowKey={(record, i) => record.feature ?? record.__uid ?? String(i)}
        footer={() =>
          readonly ? null : (
            <Button
              onClick={() =>
                editor.append(
                  MapCadastralAreaItemSchema.cast(
                    { isManual: true },
                    { assert: false },
                  ),
                )
              }
            >
              Aggiungi
            </Button>
          )
        }
        summary={
          editor.items.length > 0
            ? () => {
                return (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={6} colSpan={6}>
                      <Flex align="center" justify="flex-end">
                        <span>Totale aree interessate:</span>
                        <MapComputedTotalCadastralAffectedArea
                          name={name}
                          readonly={readonly}
                        />
                      </Flex>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }
            : undefined
        }
        columns={[
          {
            title: 'Comune (cod. catastale)',
            dataIndex: 'castralMunicipality',
            key: 'castralMunicipality',
            render: (value: string, record, index) =>
              record.isManual && !readonly ? (
                <FormItem
                  name={`${name}${MapCadastralSuffix}.${index}.castralMunicipality`}
                  label="Codice catastale"
                >
                  <TextInput
                    name={`${name}${MapCadastralSuffix}.${index}.castralMunicipality`}
                    readOnly={readonly}
                  />
                </FormItem>
              ) : (
                value
              ),
          },
          {
            title: 'Sezione catastale',
            dataIndex: 'cadastralSection',
            key: 'cadastralSection',
            render: (value: string, record, index) =>
              record.isManual && !readonly ? (
                <FormItem
                  name={`${name}${MapCadastralSuffix}.${index}.cadastralSection`}
                  label="Sezione catastale"
                >
                  <TextInput
                    name={`${name}${MapCadastralSuffix}.${index}.cadastralSection`}
                    readOnly={readonly}
                  />
                </FormItem>
              ) : (
                value
              ),
          },
          {
            title: 'Foglio catastale',
            dataIndex: 'cadastralSheet',
            key: 'cadastralSheet',
            render: (value: string, record, index) =>
              record.isManual && !readonly ? (
                <FormItem
                  name={`${name}${MapCadastralSuffix}.${index}.cadastralSheet`}
                  label="Foglio catastale"
                >
                  <TextInput
                    name={`${name}${MapCadastralSuffix}.${index}.cadastralSheet`}
                    readOnly={readonly}
                  />
                </FormItem>
              ) : (
                value
              ),
          },
          {
            title: 'Particella catastale',
            dataIndex: 'cadastralParticle',
            key: 'cadastralParticle',
            render: (value: string, record, index) =>
              record.isManual && !readonly ? (
                <FormItem
                  name={`${name}${MapCadastralSuffix}.${index}.cadastralParticle`}
                  label="Particella catastale"
                >
                  <TextInput
                    name={`${name}${MapCadastralSuffix}.${index}.cadastralParticle`}
                    readOnly={readonly}
                  />
                </FormItem>
              ) : (
                value
              ),
          },
          {
            title: 'Area',
            dataIndex: 'area',
            key: 'area',
            render: (value: number) => <MapUnitRender value={value} />,
            width: '150px',
          },
          {
            title: 'Area interessata',
            dataIndex: 'affectedArea',
            key: 'affectedArea',
            width: '200px',
            render: (value: number, record, index) => (
              <Space>
                <FormItem
                  name={`${name}${MapCadastralSuffix}.${index}.affectedArea`}
                  label="Area interessata"
                  readonly={readonly}
                  layout={{
                    removeHelpMargin: true,
                    removeLabelSpace: true,
                    hideVisualLabel: true,
                  }}
                >
                  <DecimalInput
                    name={`${name}${MapCadastralSuffix}.${index}.affectedArea`}
                    readOnly={readonly}
                    suffix="mq"
                  />
                </FormItem>
                {record.isManual && !readonly && (
                  <Tooltip title="Rimuovi area">
                    <Button
                      icon={<DeleteOutlined />}
                      danger
                      onClick={() => {
                        editor.removeAt(index);
                      }}
                    />
                  </Tooltip>
                )}
              </Space>
            ),
          },
        ]}
      />
    </div>
  );
}
